import { useMemo, useState } from 'react';
import { AssetPortfolio, AssetPortfolioComponent } from '../../type/AssetPortfolio.type';
import AddAssetPortfolioComponentModal, {
  AddAssetPortfolioComponentModalData,
} from './AddAssetPortfolioComponentModal';
import { ASSET_REPORT_LABEL } from '../../../../common/comstant/LabelConstants';
import { AssetItem, LiabilityItem, REPORT_ITEM_TYPE } from '../../../assetReport/type/AssetReport.type';
import { getAssetPortfolioComponentItemList } from '../../util/AssetPortfolioUtils';
import {
  useAppStateAssetPortfolioListStore,
  useAppStateCurrentAssetReportStore,
} from '../../../../common/store/AppStateStore.zustand';
import { useAssetPortfolioAPI } from '../../api/AssetPortfolio.api';

export interface EditAssetPortfolioModalData {
  isOpen: boolean;
  assetPortfolio: AssetPortfolio;
  onClose: () => void;
}

const EditAssetPortfolioModal = ({ isOpen, assetPortfolio, onClose }: EditAssetPortfolioModalData) => {
  const { updateAssetPortfolio, getAssetPortfolioList } = useAssetPortfolioAPI();
  const { currentAssetReport } = useAppStateCurrentAssetReportStore();
  const { setAssetPortfolioList } = useAppStateAssetPortfolioListStore();
  const [addAssetPortfolioComponentModal, setAddAssetPortfolioComponentModal] =
    useState<AddAssetPortfolioComponentModalData>();
  const [editAssetPortfolio, setEditAssetPortfolio] = useState<AssetPortfolio>(assetPortfolio);

  const totalRate = useMemo(
    () => editAssetPortfolio.componentList.map((component) => component.targetRate).reduce((sum, cur) => sum + cur, 0),
    [editAssetPortfolio.componentList],
  );

  const validInputYn = useMemo(
    () => editAssetPortfolio.name && editAssetPortfolio.componentList?.length,
    [editAssetPortfolio],
  );

  const handleCloseClick = () => {
    onClose();
  };

  const handleEditClick = async () => {
    await updateAssetPortfolio(editAssetPortfolio.id, editAssetPortfolio);
    const { data } = await getAssetPortfolioList();
    setAssetPortfolioList(data);
    onClose();
  };

  const handleNewAssetPortfolioChange = (target: string, value: any) => {
    setEditAssetPortfolio({
      ...editAssetPortfolio,
      [target]: value,
    });
  };

  const handleEditComponentClick = (componentIndex: number, component: AssetPortfolioComponent) => {
    setAddAssetPortfolioComponentModal({
      isOpen: true,
      assetPortfolioComponent: {
        id: component.id,
        assetPortfolioId: component.assetPortfolioId,
        name: component.name,
        targetRate: component.targetRate,
        conditionList: component.conditionList,
      },
      onClose: (assetPortfolioComponent?: AssetPortfolioComponent) => {
        assetPortfolioComponent &&
          handleNewAssetPortfolioChange('componentList', [
            ...editAssetPortfolio.componentList.map((componentItem, index) =>
              index === componentIndex ? assetPortfolioComponent : componentItem,
            ),
          ]);
        setAddAssetPortfolioComponentModal(undefined);
      },
    });
  };

  const handleDeleteClick = (componentIndex: number) => {
    handleNewAssetPortfolioChange(
      'componentList',
      editAssetPortfolio.componentList.filter((component, index) => index !== componentIndex),
    );
  };

  const handleAddComponentClick = () => {
    setAddAssetPortfolioComponentModal({
      isOpen: true,
      assetPortfolioComponent: {
        id: '',
        assetPortfolioId: editAssetPortfolio.id,
        name: '',
        targetRate: 0,
        conditionList: [],
      },
      onClose: (assetPortfolioComponent?: AssetPortfolioComponent) => {
        assetPortfolioComponent &&
          handleNewAssetPortfolioChange('componentList', [
            ...editAssetPortfolio.componentList,
            assetPortfolioComponent,
          ]);
        setAddAssetPortfolioComponentModal(undefined);
      },
    });
  };

  return (
    <div className="fixed w-full md:max-w-[800px] bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-800">포트폴리오 수정</h2>
            <button onClick={handleCloseClick} className="text-gray-400 hover:text-gray-600">
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
        <div className="p-6">
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">이름</label>
              <input
                type="text"
                value={editAssetPortfolio.name || ''}
                onChange={(e) => handleNewAssetPortfolioChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                placeholder="예: 안정성장형 포트폴리오"
              />
            </div>
            <div className="border-t border-gray-200 pt-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">구성 요소</h3>
                <button
                  onClick={handleAddComponentClick}
                  className="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-md text-sm flex items-center !rounded-button whitespace-nowrap"
                >
                  <i className="fas fa-plus mr-2"></i> 구성요소 추가
                </button>
              </div>
              {editAssetPortfolio.componentList && editAssetPortfolio.componentList.length > 0 ? (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-4">
                    {editAssetPortfolio.componentList.map((component, index) => (
                      <div key={index} className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="flex justify-between items-center mb-2">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center mr-3">
                              <i className="fas fa-chart-pie text-indigo-500"></i>
                            </div>
                            <span className="font-medium text-gray-800">{component.name}</span>
                          </div>
                          <div className="flex items-center">
                            <span className="text-lg font-semibold text-indigo-600">{component.targetRate}%</span>
                            <button
                              onClick={() => handleEditComponentClick(index, component)}
                              className="ml-4 text-gray-400 hover:text-blue-500"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              onClick={() => handleDeleteClick(index)}
                              className="ml-4 text-gray-400 hover:text-red-500"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                        </div>
                        <div className="mt-4">
                          <button
                            onClick={(e) => {
                              const target = e.currentTarget.nextElementSibling as HTMLElement;
                              if (target) {
                                target.style.display = target.style.display === 'none' ? 'grid' : 'none';
                              }
                              const icon = e.currentTarget.querySelector('i.fa-chevron-down');
                              if (icon) {
                                icon.classList.toggle('rotate-180');
                              }
                            }}
                            className="w-full flex items-center justify-between text-sm font-medium text-gray-700 mb-2 hover:text-gray-900"
                          >
                            <span>
                              포함 자산
                              <span className="ml-1 text-xs text-gray-500">
                                (총
                                {
                                  getAssetPortfolioComponentItemList(component, currentAssetReport?.reportItemList)
                                    .length
                                }
                                개의 자산)
                              </span>
                            </span>
                            <i className="fas fa-chevron-down transition-transform duration-200"></i>
                          </button>
                          <div className="grid grid-cols-2 gap-2">
                            {getAssetPortfolioComponentItemList(component, currentAssetReport?.reportItemList).map(
                              (reportItem) => (
                                <div className="bg-white rounded p-3 shadow-sm shadow-md">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-gray-700">{reportItem.name}</span>
                                  </div>
                                  <div className="flex items-center text-xs text-gray-500">
                                    <i className="fas fa-user mr-1"></i>
                                    <span>{ASSET_REPORT_LABEL[reportItem.owner]}</span>
                                    <span className="mx-2">•</span>
                                    <span>{ASSET_REPORT_LABEL[reportItem.type]}</span>
                                    <span className="mx-2">•</span>
                                    <span>
                                      {
                                        ASSET_REPORT_LABEL[
                                          reportItem.type === REPORT_ITEM_TYPE.ASSET
                                            ? (reportItem as AssetItem).assetType
                                            : (reportItem as LiabilityItem).liabilityType
                                        ]
                                      }
                                    </span>
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 p-4 bg-indigo-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-indigo-800">총 구성 비율</p>
                        <p className="text-xs text-indigo-600 mt-1">모든 구성요소의 비율 합</p>
                      </div>
                      <p className="text-2xl font-bold text-indigo-700">{totalRate}%</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="fas fa-chart-pie text-gray-400 text-2xl"></i>
                  </div>
                  <p className="text-gray-500">아직 추가된 구성요소가 없습니다</p>
                  <p className="text-sm text-gray-400 mt-1">구성요소 추가 버튼을 클릭하여 포트폴리오를 구성하세요</p>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="p-6 border-t border-gray-200">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 !rounded-button whitespace-nowrap"
            >
              취소
            </button>
            <button
              onClick={handleEditClick}
              disabled={!validInputYn}
              className={`text-white px-4 py-2 rounded-md text-sm !rounded-button whitespace-nowrap ${
                validInputYn ? 'bg-indigo-500 hover:bg-indigo-600' : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              포트폴리오 수정
            </button>
          </div>
        </div>
      </div>
      {addAssetPortfolioComponentModal && <AddAssetPortfolioComponentModal {...addAssetPortfolioComponentModal} />}
    </div>
  );
};

export default EditAssetPortfolioModal;
